import { useNavigate } from "react-router-dom";
import { formatHoursToHM } from "../../pages/resourceOverview/ResourceOverview";
import { useState } from "react";

interface TeamAvailabilityProps {
    member: any
    startDate?: string
    endDate?: string
}

interface SkillBadgeProps {
    skills: string[]
}

const SkillsTooltip: React.FC<SkillBadgeProps> = ({ skills }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const visibleSkills = skills.slice(0, 2);
    const remainingCount = skills.length - 2;

    return (
        <div className="self-stretch flex items-start flex-wrap content-start gap-[8px] w-full relative">
            {visibleSkills.map((skill: string, index: number) => (
                <div key={index} className="bg-white rounded-[100px] viewbg-dv right_status_clockout justify-center py-1 px-2 text-sm text-white flex flex-col items-start py-num-0 pl-num-0 pr-num-8">
                    <div className="self-stretch flex flex-col items-start py-num-4 px-num-8">
                        <div className="relative font-semibold p-purple-clr-dv">{skill}</div>
                    </div>
                </div>
            ))}
            {remainingCount > 0 && (
                <div 
                    className="relative bg-white rounded-[100px] viewbg-dv right_status_clockout justify-center py-1 px-2 text-sm text-white flex flex-col items-start py-num-0 pl-num-0 pr-num-8 cursor-pointer"
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                    style={{ zIndex: showTooltip ? 9999 : 'auto' }}
                >
                    <div className="self-stretch flex flex-col items-start py-num-4 px-num-8">
                        <div className="relative font-semibold p-purple-clr-dv">+{remainingCount}</div>
                    </div>
                    {showTooltip && (
                        <div 
                            className="absolute bottom-full left-0 mb-2 p-3 tooltip-ui-dv dark:bg-gray-800 text-white rounded-lg shadow-xl min-w-[200px] max-w-[300px]"
                            style={{ 
                                zIndex: 99999,
                                pointerEvents: 'none'
                            }}
                        >
                            <div className="flex flex-wrap gap-2">
                                {skills.slice(2).map((skill: string, index: number) => (
                                    <span key={index} className="text-xs dark:bg-gray-700 text-tooltip-ui-dv px-2 py-1 dark:text-white rounded whitespace-nowrap">
                                        {skill}
                                    </span>
                                ))}
                            </div>
                            <div className="absolute bottom-[-6px] left-4 w-3 h-3 dark:bg-gray-800 transform rotate-45"></div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const TeamMemberCard: React.FC<TeamAvailabilityProps> = ({ member, startDate, endDate }) => {
    const navigate = useNavigate()
    const availableHours = member?.total_working_hours - member?.allocated_working_hours;

    return (
        <div className="self-stretch flex-1 services-item-box flex flex-col items-start  gap-[8px] text-dimgray-200 p-4"
            onClick={() => {
                // clear old persisted values
                localStorage.removeItem("teamAvailabilityStartDate");
                localStorage.removeItem("teamAvailabilityEndDate");
                localStorage.removeItem("teamAvailabilityEmployeeID")
                localStorage.removeItem("teamAvailabilityEmployeeName")

                navigate('/team_availability', {
                    state: {
                        resStartDate: startDate,
                        resEndDate: endDate,
                        resSelectedEmployeeID: member?.employee_id,
                        resSelectedEmployeeName: member && `${member?.first_name} ${member?.last_name}`
                    }
                })
            }}
        >
            <div className="self-stretch  overflow-hidden flex flex-col items-center ">
                <div className="self-stretch flex items-center justify-between gap-0">
                    <div className="flex items-center">
                        <div className=" overflow-hidden flex flex-col items-start py-num-0 pl-num-16 pr-num-0 text-num-24 text-black">
                            <div className="flex flex-col items-start">
                                <h5 className="flex-1 flex items-center my-0">
                                    <b className="relative">{member && `${member?.first_name} ${member?.last_name}`}</b>
                                </h5>
                            </div>
                        </div>
                    </div>
                    {member && member?.is_available === true && (
                        <div className="rounded-num-100 bg-mediumseagreen  flex items-center justify-center py-num-4 px-num-8 text-[14px]">
                            <div className="relative  bg-green-500 p-2 rounded-81xl text-white">Available</div>
                        </div>
                    )}

                    {member && member?.is_over_utilized === true && (
                        <div className="rounded-[100px] bg-crimson flex flex-row items-center justify-center text-sm text-white right_status_clockout">
                            <div className="relative py-1 px-2 rounded-81xl text-white">Busy</div>
                        </div>
                    )}
                </div>
            </div>

            <div className="ml-0 pl-0  cts-border-blg  min-w-full bordert-services-dv">
                <ul className="ml-0 pl-0 resource-ul-dv grid grid-cols-2 mq1400:grid-2 mq1275:grid-cols-2 mq950:grid-cols-2 mq575:grid-cols-1 self-stretch flex-row  gap-[8px] mq991:gap-[8px] text-lg my-0">
                    <li className="self-stretch grid items-start gap-2  justify-start">
                        <h1 className="relative font-semibold my-0 text-gray round-box-resource"><span>{member && formatHoursToHM(member?.total_working_hours)}</span></h1>
                        <div className=" relative shrink-0 text-left"><span>Total Hours</span> </div>

                    </li>
                    <li className="self-stretch grid items-start justify-start gap-2">
                        <h1 className="relative font-semibold my-0 text-gray round-box-resource"><span>{member && formatHoursToHM(member?.allocated_working_hours)}</span></h1>
                        <div className=" relative shrink-0 text-left"><span>Allocated Hours</span> </div>
                    </li>
                    <li className="self-stretch grid items-start gap-2  justify-start">
                        <h1 className="relative font-semibold my-0 text-gray round-box-resource"><span>{formatHoursToHM(availableHours)}</span></h1>
                        <div className=" relative shrink-0 text-left"><span>Available Hours</span></div>
                    </li>
                </ul>

                {member && member?.skill_set?.length > 0 && (
                    <div className="self-stretch flex w-full" onClick={(e) => e.stopPropagation()}>
                        <div className="self-stretch flex flex-col items-start w-full">
                            <p className="dark:text-white font-semibold text-black">Skills</p>
                            <SkillsTooltip skills={member.skill_set} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default TeamMemberCard;